import { Link } from 'react-router-dom';
import {
  Container,
  Title,
  Table,
  Badge,
  Card,
  Text,
  Stack,
  Group,
  Anchor,
  Loader,
  Alert,
  Button,
  Pagination,
} from '@mantine/core';
import { IconExclamationCircle, IconReceipt, IconBuildingStore } from '@tabler/icons-react';
import { useState } from 'react';
import { useAuth } from '@clerk/react';
import { useApi } from '../../api';
import ProtectedRoute from '../../components/auth/ProtectedRoute';
import { formatCents } from '../../components/products/productDisplay';

const PAGE_LIMIT = 20;

const formatDate = (dateString) => {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

// One PurchaseRecord row (contract §3.16). Sellers link to the princess
// detail page; album purchases link to the album page. Boost rows (§4.21)
// have no seller — the platform is the counterparty.
const PurchaseRow = ({ record }) => {
  const sellerLink = record.seller ? (
    <Anchor component={Link} to={`/princess/${record.seller.id}`} size="sm">
      {record.seller.display_name}
    </Anchor>
  ) : null;

  return (
    <Table.Tr>
      <Table.Td>{formatDate(record.created_at)}</Table.Td>
      <Table.Td>
        {record.type === 'tip' ? (
          <Badge color="pink" variant="light">Tip</Badge>
        ) : record.type === 'boost' ? (
          <Badge color="yellow" variant="light">Boost</Badge>
        ) : (
          <Badge color="grape" variant="light">Album</Badge>
        )}
      </Table.Td>
      <Table.Td>
        {record.type === 'tip' ? (
          <Text size="sm">Tip to {sellerLink}</Text>
        ) : record.type === 'boost' ? (
          <Text size="sm">Profile Boost ({record.boost?.days ?? '—'} days)</Text>
        ) : (
          <Text size="sm">
            Album:{' '}
            {record.album ? (
              <Anchor component={Link} to={`/albums/${record.album.id}`} size="sm">
                {record.album.title}
              </Anchor>
            ) : (
              '—'
            )}
            {' — '}
            {sellerLink}
          </Text>
        )}
      </Table.Td>
      <Table.Td>
        <Text size="sm" fw={600}>{formatCents(record.amount_cents)}</Text>
      </Table.Td>
      <Table.Td>
        {record.status === 'completed' ? (
          <Badge color="green" variant="light">Completed</Badge>
        ) : record.status === 'refunded' ? (
          // §4.20 — the charge was refunded / charged back
          <Badge color="red" variant="light">Refunded</Badge>
        ) : (
          <Badge color="yellow" variant="light">Processing</Badge>
        )}
      </Table.Td>
    </Table.Tr>
  );
};

// Buyer transaction history (contract §4.17) — GET /api/v1/me/purchases.
// Tips sent + album purchases + profile boosts, newest first, pending and
// completed alike.
const PurchasesPage = () => {
  const [page, setPage] = useState(1);
  const { isSignedIn } = useAuth();

  const { data, loading, error } = useApi(
    '/api/v1/me/purchases',
    { page, limit: PAGE_LIMIT },
    { enabled: !!isSignedIn, query: { placeholderData: (prev) => prev } }
  );
  const purchases = data?.items ?? [];
  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.limit)) : 1;

  return (
    <ProtectedRoute requireAuth={true}>
      <Container size="lg" py="xl">
        <Stack gap="xl">
          <Group gap="xs" align="center">
            <IconReceipt size="1.5rem" color="var(--mantine-color-pink-6)" />
            <Title order={1}>My Purchases</Title>
          </Group>

          {error && (
            <Alert icon={<IconExclamationCircle size="1rem" />} color="red">
              {error.message}
            </Alert>
          )}

          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
              <Loader size="lg" />
            </div>
          ) : purchases.length === 0 ? (
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Stack align="center" gap="md" py="xl">
                <IconReceipt size={48} color="var(--mantine-color-gray-4)" />
                <Text c="dimmed" size="lg">No purchases yet</Text>
                <Text c="dimmed" size="sm">
                  Tips you send and albums you unlock will show up here.
                </Text>
                <Button
                  component={Link}
                  to="/princesses"
                  color="grape"
                  variant="light"
                  leftSection={<IconBuildingStore size="1rem" />}
                >
                  Browse Princesses
                </Button>
              </Stack>
            </Card>
          ) : (
            <Card shadow="sm" padding={0} radius="md" withBorder>
              <Table.ScrollContainer minWidth={640}>
                <Table striped highlightOnHover verticalSpacing="sm">
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Date</Table.Th>
                      <Table.Th>Type</Table.Th>
                      <Table.Th>Description</Table.Th>
                      <Table.Th>Amount</Table.Th>
                      <Table.Th>Status</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {purchases.map((record) => (
                      <PurchaseRow key={`${record.type}-${record.id}`} record={record} />
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            </Card>
          )}

          {totalPages > 1 && (
            <Group justify="center">
              <Pagination value={page} onChange={setPage} total={totalPages} />
            </Group>
          )}
        </Stack>
      </Container>
    </ProtectedRoute>
  );
};

export default PurchasesPage;
